import { create } from 'zustand';
import api from '../services/api';
import type { BlockedOrganization } from '../lib/orgBlock';
import type { DateFormat } from '../types';

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  tax_number: string;
  registration_number: string;
  address_line1: string;
  address_line2: string;
  postal_code: string;
  city: string;
  country: string;
  currency: string;
  timezone: string;
  pdf_filename_pattern: string;
  excel_filename_pattern: string;
  decimal_format: string;
  date_format: DateFormat;
  /** T1130 — kaj vidijo vloge pod Manager: selling_only | selling_and_cost. */
  price_display_below_manager: 'selling_only' | 'selling_and_cost';
  /** T1138 — izbrana premium postavitev BoQ izvoza (design paket). */
  boq_layout_key?: string;
  export_defaults?: Record<string, unknown>;
  branding_slot?: Record<string, unknown>;
  is_active: boolean;
  /**
   * T1350 — ali sme organizacija sploh sprožiti brezplačni preizkus.
   *
   * Zaledje (`OrganizationSerializer`) polje vedno pošlje; neobvezno je zato,
   * ker starejše testne fiksture organizacije sestavljajo ročno. Koda ga zato
   * primerja z `=== false`, da odsotnost nikoli ne prebere kot »ni preizkusa«.
   */
  trial_available?: boolean;
  role: string;
  my_permissions: string[];
  /**
   * T2798 — zaledje prizna `billing.manage` (`CanManageBilling`): lastnik ali
   * administrator. Neobvezno zaradi ročno sestavljenih testnih fikstur;
   * odsotnost pomeni »ne«.
   */
  can_manage_billing?: boolean;
  member_count: number;
  project_count: number;
  subscription_status: string;
  created_at: string;
  /* ---- Storage usage (T886/T895) — izpostavljeno na OrganizationSerializer ---- */
  storage_used_bytes?: number;
  storage_quota_bytes?: number;
  storage_available_bytes?: number;
  storage_usage_percent?: number;
  /** Opozorilni prag (%); če ga backend ne pošlje, uporabimo privzetih 90. */
  storage_org_warn_percent?: number;
}

interface OrganizationState {
  organizations: Organization[];
  /**
   * T2459 — organizacija uporabnika. Po pravilu ene organizacije (T2456) je
   * aktivno članstvo eno samo, zato je to vedno prva s seznama (zaledje jo pri
   * starejših podatkih z več članstvi postavi na prvo mesto,
   * `one_org.user_organization`). Izbire ni — ne izbirnika ne zapomnjene
   * izbire v `localStorage`.
   */
  currentOrganization: Organization | null;
  isLoading: boolean;
  /**
   * T2456 — seznam je bil vsaj enkrat uspešno naložen. Loči »uporabnik nima
   * organizacije« (prazen uspešen odgovor) od »seznama ni bilo mogoče
   * naložiti« (napaka, npr. 403 ob zaklepu) — samo prvo sme pokazati zaslon
   * brez organizacije.
   */
  loaded: boolean;
  fetchOrganizations: () => Promise<void>;
  /** T1195 — zasilna identiteta iz telesa 403; gl. `seedFromBlockedOrganization`. */
  seedFromBlockedOrganization: (blocked: BlockedOrganization) => void;
  reset: () => void;
}

/**
 * T1195 — organizacija, sestavljena **izključno** iz telesa 403.
 *
 * Ko je organizacija suspendirana, `organizations/` vrne 403 in
 * `currentOrganization` ostane `null`; `BillingPage` se v tem primeru sploh ne
 * izriše. Plačilna pot iz zaklepa je zato prekinjena tam, kjer bi se morala
 * začeti.
 *
 * Objekt je **namerno nepopoln**. Vsa polja, ki jih telo ne nosi, ostanejo
 * `undefined` — ne dobijo privzetkov. Napačen `country` bi pomenil napačno
 * predlagano plačilno metodo, napačen `decimal_format` napačno prikazan
 * znesek; `undefined` odjemalca pošlje na obstoječe fallbacke (sistemske
 * nastavitve), izmišljena vrednost pa bi tiho lagala.
 *
 * `is_active: false` ni ugibanje, ampak edino, kar o stanju zanesljivo vemo.
 */
function organizationFromBlock(blocked: BlockedOrganization): Organization {
  return {
    id: blocked.id,
    name: blocked.name,
    slug: blocked.slug,
    country: blocked.country,
    role: blocked.role ?? '',
    my_permissions: blocked.my_permissions,
    // T2798: brez tega bi administrator zaklenjene organizacije izgubil
    // plačilno pot iz zaklepa, ki mu jo zaledje priznava.
    can_manage_billing: blocked.can_manage_billing,
    is_active: false,
  } as Organization;
}

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
  organizations: [],
  currentOrganization: null,
  isLoading: false,
  loaded: false,

  fetchOrganizations: async () => {
    set({ isLoading: true });
    try {
      const response = await api.get('organizations/');
      const organizations: Organization[] = response.data.results;

      // T2459: trenutna organizacija je organizacija uporabnika — prva s
      // SVEŽEGA seznama (zaledje jo postavi na prvo mesto). Nikoli ne ostane
      // stara iz pomnilnika: ob menjavi računa bi pripadala drugemu najemniku
      // in bi njegove podatke prenesla v novo sejo (cross-tenant).
      const newCurrent: Organization | null = organizations[0] ?? null;

      set({ organizations, currentOrganization: newCurrent, loaded: true });
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  seedFromBlockedOrganization: (blocked) => {
    // Samo zasilno: če je pravi odgovor `organizations/` že prišel, je popolnejši
    // in ga nepopolna zasilna kopija ne sme povoziti.
    if (get().currentOrganization) return;
    set({ currentOrganization: organizationFromBlock(blocked) });
  },

  reset: () => {
    // T2459: ključa izbire ne pišemo več; starega, ki ga je pustila različica
    // pred T2459, ob odjavi vseeno počistimo.
    localStorage.removeItem('current_org_slug');
    set({ organizations: [], currentOrganization: null, isLoading: false, loaded: false });
  },
}));
